'use client'

import * as React from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/utils/styles/cn'
import { CommandShortcut } from '@/components/ui/command'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { AnimatePresence, motion } from 'framer-motion'
import { DimensionsFadeContainer } from '@/features/animations/dimensions-fade-container'
import { run } from 'shared'

export type IButtonVariant = VariantProps<typeof buttonVariants>['variant']

// Each ds2 size sets `--btn-icon-size` (read by `[&_svg]:size-[var(--btn-icon-size,…)]`
// in baseStyles) so lucide icons scale down with the button instead of stamping at 18px.
// Non-ds2 sizes don't set it and inherit the 18px fallback (existing behavior).
const DS2_XS_SIZE = 'h-[22px] px-[8px] py-[2px] text-[11px] [--btn-icon-size:12px]'
const DS2_SM_SIZE = 'h-[28px] px-[9px] py-[5px] text-[12px] [--btn-icon-size:14px]'
const DS2_SIZE = 'h-[32px] px-[10px] py-[6px] text-[13px] [--btn-icon-size:14px]'
const DS2_LG_SIZE = 'h-[40px] px-[10px] py-[8px]'
const withBreakpoint = (bp: string, classes: string) =>
    classes
        .split(' ')
        .map((c) => `${bp}:${c}`)
        .join(' ')

// Icons inside a Button pick up their size from `--btn-icon-size` (set per size variant,
// fallback 1.125rem = 18px). Callers no longer need to write `h-3.5 w-3.5` on every lucide
// icon — the button's size dictates it. To override on a one-off, pass the variable via
// style: `<Button style={{ '--btn-icon-size': '16px' }}>` (no `!important` needed).
const baseStyles =
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-[var(--btn-icon-size,1.125rem)] [&_svg]:shrink-0 m-px'

const buttonVariants = cva('', {
    variants: {
        variant: {
            primary: cn(
                baseStyles,
                'stroke-primary-foreground text-primary-foreground disabled:text-primary-foreground/80 bg-[#FF631B] shadow-[0px_0.75px_0px_0px_rgba(255,255,255,0.20)_inset,0px_1px_2px_0px_rgba(0,0,0,0.40),0px_0px_0px_1px_#DE600A] hover:brightness-110 transition-opacity transition-colors disabled:opacity-70'
            ),
            conversion: cn(
                baseStyles,
                '[background:var(--buttons-button-conversion,#2563EB)]',
                'shadow-[0_0_0_1px_var(--blue-700,#1D4ED8),0_1px_3px_0_rgba(33,33,38,0.20),0_1px_0_0_rgba(255,255,255,0.07)_inset]',
                'stroke-white text-white disabled:text-white/80',
                'hover:brightness-110 transition-opacity transition-colors disabled:opacity-70'
            ),
            brand: cn(
                baseStyles,
                'bg-brand-500 text-primary-foreground stroke-primary-foreground shadow-sm hover:bg-brand-600 disabled:bg-brand-400 disabled:text-primary-foreground/80 overflow-hidden group transition-all duration-300 hover:scale-[1] active:scale-[0.98]'
            ),
            'brand-secondary': cn(
                baseStyles,
                'text-[#CE5000] stroke-[#CE5000] border border-[#CE5000] bg-transparent shadow-[0px_0.75px_0px_0px_rgba(255,255,255,0.20)_inset,0px_1px_2px_0px_rgba(0,0,0,0.40),0px_0px_0px_1px_#DE600A] hover:brightness-110 transition-opacity transition-colors disabled:opacity-70'
            ),
            default: cn(
                baseStyles,
                'bg-primary text-primary-foreground stroke-primary-foreground shadow-sm hover:bg-primary/90'
            ),
            destructive: cn(
                baseStyles,
                'stroke-primary-foreground text-primary-foreground disabled:text-primary-foreground/80 bg-[radial-gradient(228.59%_228.57%_at_50%_0%,#EF4444_0%,#B91C1C_100%)] shadow-[0px_0.75px_0px_0px_rgba(255,255,255,0.20)_inset,0px_1px_2px_0px_rgba(0,0,0,0.40),0px_0px_0px_1px_#DC2626] hover:brightness-110 transition-opacity transition-colors disabled:opacity-70'
            ),
            outline: cn(
                baseStyles,
                'border border-black/10 bg-background shadow-xs hover:bg-accent hover:text-accent-foreground disabled:text-accent-foreground/50 stroke-black'
            ),
            secondary: cn(
                baseStyles,
                'bg-secondary text-secondary-foreground stroke-secondary-foreground shadow-xs hover:bg-secondary/60 border border-white'
            ),
            ghost: cn(baseStyles, 'hover:bg-accent hover:text-accent-foreground stroke-accent'),
            link: cn(baseStyles, 'text-primary underline-offset-4 hover:underline stroke-primary'),
            magic: cn(
                baseStyles,
                'bg-linear-to-r from-blue-500 to-brand-500 text-white stroke-white shadow-sm overflow-hidden group transition-all duration-300 relative after:absolute after:inset-0 after:bg-white/20 after:opacity-0 hover:after:opacity-100 after:transition-opacity after:duration-300 hover:shadow-md'
            ),
            'ds2-primary': cn(
                baseStyles,
                'isolate overflow-hidden rounded-[8px]',
                'bg-[#161514] text-white stroke-white',
                'gap-[6px]',
                'border border-black',
                'shadow-[0px_1px_3px_rgba(33,33,38,0.20),inset_0px_1px_0px_rgba(255,255,255,0.07)]',
                'before:absolute before:inset-0 before:rounded-[8px] before:opacity-50 before:content-[\"\"]',
                'before:[background:linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0)_100%)]',
                'after:absolute after:inset-0 after:rounded-[8px] after:content-[\"\"]',
                'after:[background:linear-gradient(180deg,rgba(255,255,255,0.1)_46%,rgba(255,255,255,0)_54%)] after:[mix-blend-mode:overlay]',
                'transition-colors duration-200 ease-out',
                'hover:bg-[#2A2928] active:bg-[#131211]',
                'before:transition-opacity after:transition-opacity before:duration-200 after:duration-200',
                'hover:before:opacity-75 active:before:opacity-45',
                'hover:after:opacity-100 active:after:opacity-90'
            ),
            'ds2-conversion': cn(
                baseStyles,
                'isolate overflow-hidden rounded-[8px]',
                'text-white stroke-white',
                'gap-[6px]',
                'border-[color:var(--blue-700,#1D4ED8)]',
                '[background:var(--buttons-button-conversion,#2563EB)]',
                'shadow-[0_1px_3px_rgba(33,33,38,0.20),inset_0_1px_0_rgba(255,255,255,0.07)]',
                'before:absolute before:inset-0 before:rounded-[8px] before:opacity-50 before:content-[\"\"]',
                'before:[background:linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0)_100%)]',
                'after:absolute after:inset-0 after:rounded-[8px] after:content-[\"\"]',
                'after:[background:linear-gradient(180deg,rgba(255,255,255,0.1)_46%,rgba(255,255,255,0)_54%)] after:[mix-blend-mode:overlay]',
                'transition-colors duration-200 ease-out',
                'hover:brightness-110 active:brightness-95',
                'before:transition-opacity after:transition-opacity before:duration-200 after:duration-200',
                'hover:before:opacity-75 active:before:opacity-45',
                'hover:after:opacity-100 active:after:opacity-90'
            ),
            'ds2-success': cn(
                baseStyles,
                'isolate overflow-hidden rounded-[8px]',
                'bg-[#059669] text-white stroke-white',
                'gap-[6px]',
                'border border-[#047857]',
                'shadow-[0px_1px_3px_rgba(33,33,38,0.20),inset_0px_1px_0px_rgba(255,255,255,0.07)]',
                'before:absolute before:inset-0 before:rounded-[8px] before:opacity-50 before:content-[\"\"]',
                'before:[background:linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0)_100%)]',
                'after:absolute after:inset-0 after:rounded-[8px] after:content-[\"\"]',
                'after:[background:linear-gradient(180deg,rgba(255,255,255,0.1)_46%,rgba(255,255,255,0)_54%)] after:[mix-blend-mode:overlay]',
                'transition-colors duration-200 ease-out',
                'hover:bg-[#0b7a5a] active:bg-[#047857]',
                'before:transition-opacity after:transition-opacity before:duration-200 after:duration-200',
                'hover:before:opacity-75 active:before:opacity-45',
                'hover:after:opacity-100 active:after:opacity-90'
            ),
            'ds2-highlight': cn(
                baseStyles,
                'isolate overflow-hidden rounded-[8px]',
                'bg-[#FF631B] text-white stroke-white',
                'gap-[6px]',
                'border border-[#DE600A]',
                'shadow-[0px_1px_3px_rgba(33,33,38,0.20),inset_0px_1px_0px_rgba(255,255,255,0.12)]',
                'before:absolute before:inset-0 before:rounded-[8px] before:opacity-50 before:content-[\"\"]',
                'before:[background:linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0)_100%)]',
                'after:absolute after:inset-0 after:rounded-[8px] after:content-[\"\"]',
                'after:[background:linear-gradient(180deg,rgba(255,255,255,0.1)_46%,rgba(255,255,255,0)_54%)] after:[mix-blend-mode:overlay]',
                'transition-colors duration-200 ease-out',
                'hover:bg-[#FF7838] active:bg-[#E85A12]',
                'before:transition-opacity after:transition-opacity before:duration-200 after:duration-200',
                'hover:before:opacity-75 active:before:opacity-45',
                'hover:after:opacity-100 active:after:opacity-90'
            ),
            'ds2-secondary': cn(
                baseStyles,
                'rounded-[10px]',
                'border border-black/10 bg-white/60 text-[#161514] stroke-[#161514]',
                'gap-[6px] shadow-none',
                'transition-colors duration-200 ease-out',
                'hover:bg-black/4 active:bg-black/6'
            ),
            'ds2-destructive': cn(
                baseStyles,
                'isolate overflow-hidden rounded-[10px]',
                'bg-[#DC2626] text-white stroke-white',
                'gap-[6px]',
                'border border-[#B91C1C]',
                'shadow-[0px_1px_3px_rgba(33,33,38,0.20),inset_0px_1px_0px_rgba(255,255,255,0.12)]',
                'before:absolute before:inset-0 before:rounded-[10px] before:opacity-50 before:content-[\"\"]',
                'before:[background:linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0)_100%)]',
                'after:absolute after:inset-0 after:rounded-[10px] after:content-[\"\"]',
                'after:[background:linear-gradient(180deg,rgba(255,255,255,0.1)_46%,rgba(255,255,255,0)_54%)] after:[mix-blend-mode:overlay]',
                'transition-colors duration-200 ease-out',
                'hover:bg-[#EF4444] active:bg-[#C52020]',
                'before:transition-opacity after:transition-opacity before:duration-200 after:duration-200',
                'hover:before:opacity-75 active:before:opacity-45',
                'hover:after:opacity-100 active:after:opacity-90'
            ),
            'lp-primary': cn(
                baseStyles,
                'rounded-[5px] gap-[10px]',
                '[background:var(--lp-btn-primary-bg,#FF5500)]',
                'text-[color:var(--lp-btn-primary-fg,#ffffff)] stroke-[color:var(--lp-btn-primary-fg,#ffffff)]',
                'transition-colors duration-200 ease-out',
                'hover:[background:var(--lp-btn-primary-bg-hover,#E64D00)]'
            ),
            'lp-secondary': cn(
                baseStyles,
                'rounded-[5px] gap-[10px]',
                '[background:var(--lp-btn-secondary-bg,#131210)]',
                'text-[color:var(--lp-btn-secondary-fg,#ffffff)] stroke-[color:var(--lp-btn-secondary-fg,#ffffff)]',
                'transition-colors duration-200 ease-out',
                'hover:[background:var(--lp-btn-secondary-bg-hover,#2A2724)]'
            ),
            unstyled: '',
        },
        size: {
            default: 'h-9 px-4 py-2',
            sm: 'h-8 rounded-md px-3 text-xs',
            lg: 'h-10 rounded-md px-8',
            icon: 'h-9 w-9 rounded-[8px]',
            unstyled: '',
            xs: 'h-7 rounded-md px-3 text-xs',
            'ds2-xs': DS2_XS_SIZE,
            'ds2-sm': DS2_SM_SIZE,
            ds2: DS2_SIZE,
            'ds2-lg': DS2_LG_SIZE,
            'ds2-responsive': cn(DS2_LG_SIZE, withBreakpoint('md', DS2_SIZE)),
            lp: 'h-[32px] px-[16px] pt-[3px] pb-[4px] text-[16px] font-normal leading-[1.25] tracking-[-0.02em]',
            'lp-lg': 'h-[40px] px-[20px] text-[16px] font-normal leading-[1.25] tracking-[-0.02em]',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
})

export type ICmdStyle = 'dark' | 'light'

export interface IButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean
    isLoading?: boolean
    setIsLoading?: (isLoading: boolean) => void
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<unknown>
    cmd?: string | React.ReactNode
    cmdClassName?: string
    cdmStyle?: ICmdStyle
    childContainerClassName?: string
    loadingOpacity?: number
    as?: React.ElementType
    loadingContdown?: number
    countdownText?: string
    inlineEndNode?: React.ReactNode
    href?: string
    target?: React.HTMLAttributeAnchorTarget
    rel?: string
}

const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
    (
        {
            as,
            className,
            onClick: propsOnClick,
            variant,
            size,
            asChild = false,
            isLoading = false,
            setIsLoading,
            cmd,
            cmdClassName,
            childContainerClassName,
            cdmStyle,
            loadingOpacity = 0,
            loadingContdown,
            countdownText,
            inlineEndNode,
            href,
            target,
            rel,
            ...props
        },
        ref
    ) => {
        const [countdown, setCountdown] = React.useState<number | null>(null)

        React.useEffect(() => {
            if (isLoading && loadingContdown) {
                setCountdown(loadingContdown)

                const interval = setInterval(() => {
                    setCountdown((prev) => (typeof prev === 'number' ? Math.max(prev - 1, 0) : null))
                }, 1000)

                return () => clearInterval(interval)
            }
        }, [isLoading, loadingContdown])

        const componentId = React.useId()
        const Comp = as ? as : asChild ? Slot : href ? 'a' : 'button'
        // Radix targets the caller element. Its clone keeps the caller's children in our animated wrapper.
        const slottedChild =
            asChild &&
            React.isValidElement<{ children?: React.ReactNode }>(props.children) &&
            props.children.type !== React.Fragment
                ? props.children
                : null

        if (asChild && !slottedChild) {
            throw new Error('Button with asChild expects one valid non-Fragment React element child.')
        }

        const onClick = React.useCallback(
            (e: React.MouseEvent<HTMLButtonElement>) => {
                if (isLoading) {
                    e.preventDefault()
                    return
                }

                if (propsOnClick) {
                    const promise = propsOnClick(e)

                    if (promise instanceof Promise && setIsLoading) {
                        setIsLoading(true)
                        promise.finally(() => setIsLoading(false))
                    }
                }
            },
            [propsOnClick, isLoading, setIsLoading]
        )

        const content = (
            <motion.div
                initial={{ opacity: isLoading ? loadingOpacity : 1 }}
                animate={{ opacity: isLoading ? loadingOpacity : 1 }}
                className={cn('flex items-center justify-center gap-2 w-full select-none', childContainerClassName)}
            >
                {slottedChild ? slottedChild.props.children : props.children}

                {cmd && (
                    <CommandShortcut
                        className={cn(
                            'rounded-[6px] bg-white/10 px-1.5 py-0.5',
                            cdmStyle === 'dark' ? 'bg-black/10 text-black/80' : 'bg-white/10 text-white',
                            cmdClassName
                        )}
                    >
                        {cmd}
                    </CommandShortcut>
                )}

                {inlineEndNode}
            </motion.div>
        )

        return (
            <Comp
                className={cn('relative', buttonVariants({ variant, size, className }))}
                ref={ref}
                onClick={onClick}
                onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                    if (e.key !== 'Enter') {
                        e.preventDefault()
                    }
                }}
                {...props}
                {...(href ? { href, target, rel } : {})}
                style={{
                    ...(variant === 'secondary' && {
                        boxShadow:
                            '0px 0px 0px 1px #00000014, 0px 0.75px 0px 0px #FFFFFF inset, 0px 1px 2px 0px #00000029',
                    }),
                    ...(variant === 'primary' && {
                        boxShadow:
                            '0px 0px 0px 1px #BF6323, box-shadow: 0px 1px 2px 0px #00000066, 0px 0.75px 0px 0px #FFFFFF33 inset',
                    }),
                    ...(variant === 'destructive' && {
                        boxShadow:
                            '0px 0px 0px 1px #DC2626, 0px 1px 2px 0px #00000066, 0px 0.75px 0px 0px #FFFFFF33 inset',
                    }),
                    ...props.style,
                }}
                disabled={isLoading || props.disabled}
            >
                <AnimatePresence mode="popLayout">
                    {isLoading && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            key={'button-loading-spinner' + componentId}
                            className="absolute z-1 stroke-inherit left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1.5"
                        >
                            <LoadingSpinner colorClassName="stroke-inherit" size={20} />

                            {typeof countdown === 'number' && typeof loadingContdown === 'number' && (
                                <DimensionsFadeContainer
                                    childrenKey={String(countdown).length}
                                    animateOpacity={false}
                                    animateHeight={false}
                                >
                                    <div className="text-[14px] font-medium text-inherit">
                                        {run(() => {
                                            if (countdown < 1) return 'Almost done ...'

                                            const countdownNode = <span className="font-mono">{countdown}s</span>

                                            if (!countdownText) return countdownNode

                                            return (
                                                <React.Fragment>
                                                    {countdownText} {countdownNode}
                                                </React.Fragment>
                                            )
                                        })}
                                    </div>
                                </DimensionsFadeContainer>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                {slottedChild ? <Slottable>{React.cloneElement(slottedChild, undefined, content)}</Slottable> : content}
            </Comp>
        )
    }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
